import React, { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, ImageBackground, ImageSourcePropType, StyleSheet, TouchableHighlight, View } from 'react-native';

import { Theme } from '@constants';
import { ScheduleItem } from '@types';

import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { TWCText } from '../../primitives/TWCText/TWCText';

type ContextDrawerTileProps = {
  accessibilityHint: string;
  accessibilityLabel: string;
  data: ScheduleItem;
  testID: string;
  onPress: () => void;
  onFocus: () => void;
  nextFocusUp: unknown;
  nextFocusDown: unknown;
  nextFocusLeft: unknown;
  nextFocusRight: unknown;
  liveFeedSelected: boolean;
  backgroundImage: ImageSourcePropType;
  fallbackLogo: ImageSourcePropType;
};

type ContextDrawerTileRef = View | TouchableHighlight;

const WIDTH = 320;
const HEIGHT = 180;

const LOGO = {
  width: Theme.px(72),
  minHeight: Theme.px(72),
  maxHeight: Theme.px(90),
};

export const ContextDrawerTileLayout = {
  TILE_WIDTH: Theme.px(WIDTH),
  TILE_HEIGHT: Theme.px(HEIGHT),
  TILE_SPACING: Theme.px(30),
};

const tPath = 'videoControls';

export const ContextDrawerTile = forwardRef<ContextDrawerTileRef, ContextDrawerTileProps>(
  (
    {
      accessibilityHint,
      accessibilityLabel,
      data,
      testID,
      onPress,
      onFocus,
      nextFocusUp,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      liveFeedSelected,
      backgroundImage,
      fallbackLogo,
    },
    reference,
  ) => {
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    const [logoHeight, setLogoHeight] = useState(LOGO.minHeight);

    return (
      <TWCTile
        accessibilityHint={accessibilityHint}
        accessibilityLabel={accessibilityLabel}
        height={HEIGHT}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        shouldUseContextReference={false}
        testID={testID}
        width={WIDTH}
        onFocus={onFocus}
        onPress={onPress}
      >
        <ImageBackground source={backgroundImage} style={styles.image}>
          <View style={[styles.watchingContainer, liveFeedSelected && styles.watching]}>
            {liveFeedSelected ? (
              <TWCText noTestID fontFamily={'medium'} style={styles.watchingText}>
                {t('watching')}
              </TWCText>
            ) : null}
          </View>
          <View style={styles.row}>
            <Image
              source={data?.logo ? { uri: data?.logo } : fallbackLogo}
              style={[styles.logo, { height: logoHeight }]}
              onLoad={event => {
                let width = 0;
                let height = 0;

                if (event?.nativeEvent?.source) {
                  const source = event?.nativeEvent?.source;

                  width = source.width;
                  height = source.height;
                }
                if (event?.nativeEvent?.path?.length) {
                  const img = event?.nativeEvent?.path[0];

                  width = img.naturalWidth;
                  height = img.naturalHeight;
                }
                if (width && height) {
                  const aspect = height / width;
                  const targetHeight = LOGO.width * aspect;
                  const minHeight = LOGO.minHeight;
                  const maxHeight = LOGO.maxHeight;
                  const newHeight = Math.min(Math.max(targetHeight, minHeight), maxHeight);

                  setLogoHeight(newHeight);
                }
              }}
            />
            <View style={styles.titleContainer}>
              <TWCText
                noTestID
                fontFamily={'medium'}
                numberOfLines={3}
                style={styles.titleText}
              >{`${data?.program[0].program_title}`}</TWCText>
            </View>
          </View>
        </ImageBackground>
      </TWCTile>
    );
  },
);

const styles = StyleSheet.create({
  image: {
    borderRadius: Theme.tileBorderRadius,
    height: ContextDrawerTileLayout.TILE_HEIGHT,
    overflow: 'hidden',
    paddingHorizontal: Theme.px(18),
    paddingVertical: Theme.px(11),
    resizeMode: 'cover',
    width: ContextDrawerTileLayout.TILE_WIDTH,
  },
  logo: {
    maxHeight: LOGO.maxHeight,
    minHeight: LOGO.minHeight,
    resizeMode: 'contain',
    width: LOGO.width,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: Theme.px(11),
  },
  titleContainer: {
    flex: 1,
    marginLeft: Theme.px(18),
  },
  titleText: {
    color: Theme.white,
    fontSize: Theme.px(20),
    paddingRight: Theme.px(10),
  },
  watching: {
    backgroundColor: Theme.enabledColor,
    borderRadius: Theme.px(6),
    paddingHorizontal: Theme.px(8),
    paddingVertical: Theme.px(3),
  },
  watchingContainer: {
    height: Theme.px(34),
    width: Theme.px(106),
  },
  watchingText: {
    color: Theme.black,
    fontSize: Theme.px(21),
  },
});
